import type { WorkshopCertificate } from "../../../types/content";
import { workshopCertificateImage } from "../../../utils/workshopImages";
import CertificateLightbox from "../../shared/CertificateLightbox/CertificateLightbox";
import { longDate } from "../../../utils/dateLabels";

/**
 * ============================================================================
 * The certificate showcase — a centred, framed credential (D-076).
 * ============================================================================
 *
 * Visually distinct from the workshop cards on purpose: full-width inside the
 * rail, centred, its own gradient frame and a lit ring, where the cards are a
 * left-aligned column of `card-shell`. It reads as the one piece of documentary
 * evidence on the page, which is what it is.
 *
 * ----------------------------------------------------------------------------
 * IT RENDERS WITHOUT A PICTURE, AND THAT IS NOT A PLACEHOLDER
 * ----------------------------------------------------------------------------
 * The credential IS the statement, the issuer and the date. Those are content,
 * they are present, and they render — so the showcase is complete in the sense
 * Article II.4 means, rather than an empty frame promising something absent.
 * D-069 established the shape: the video slot shipped wired and empty, and the
 * page looked finished in that state because the empty state was designed
 * rather than defaulted.
 *
 * WHAT IT DOES NOT DO IS ANNOUNCE ITS OWN BACKLOG. There is no "image pending"
 * text. A visitor is not the audience for an internal redaction queue, and a
 * documentary that narrates its own gaps in the middle of a credential is
 * making itself less credible, not more. The pending state is recorded in
 * `workshops.json`, in DECISIONS and in TODO, where the people who act on it
 * will look.
 *
 * ----------------------------------------------------------------------------
 * THE IMAGE IS GATED TWICE AND BOTH GATES ARE ELSEWHERE
 * ----------------------------------------------------------------------------
 * This component asks `workshopCertificateImage()` for a binary and renders one
 * only if `status === "published"` AND a binary comes back. It cannot be made
 * to show an unredacted scan by editing this file, because the file it would
 * need is exported from no module — see `src/utils/workshopImages.ts`, which
 * throws at module load if the two answers disagree.
 *
 * ----------------------------------------------------------------------------
 * THE LIGHTBOX MOVED OUT — 2026-09-03 (D-105)
 * ----------------------------------------------------------------------------
 * It was ~80 lines in this file: Escape, a live-queried Tab trap, focus return
 * to the trigger, and the portal into `document.body` without which a modal
 * inside a `backdrop-filter` card is not a modal. The award cards on
 * /achievements became the third consumer, and a modal is the worst thing on
 * this site to keep three copies of — what it holds is not arrangement but a
 * set of keyboard invariants that are individually easy to forget and
 * collectively the difference between a dialog and a trap (D-102).
 *
 * `CertificateLightbox` owns all of it and carries the measurements. What stays
 * here is the framing this showcase does differently: the whole certificate is
 * the trigger, where an award card uses a small thumbnail.
 */

interface CertificateShowcaseProps {
	certificate: WorkshopCertificate;
	/** The credential's own words. Rendered as a quotation, never paraphrased. */
	statement: string | null;
}

export default function CertificateShowcase({ certificate, statement }: CertificateShowcaseProps): JSX.Element {
	/* Both keys. Either one alone renders no picture. */
	const binary = certificate.status === "published" ? workshopCertificateImage(certificate.id) : undefined;

	return (
		<div className="mt-10">
			{/* The frame. A gradient shell one pixel wider than the face, which is
			    the site's card idiom (D-061) at a larger radius, plus a lit ring
			    the ordinary cards do not carry. */}
			<div className="mx-auto w-full max-w-2xl rounded-[20px] bg-gradient-to-br from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)] p-px shadow-[0_18px_48px_rgba(0,0,0,0.28)]">
				{/* `--surface-modal`, and it is OPAQUE on purpose. The first draft
				    wrote `--surface-card`, WHICH IS NOT A TOKEN — the real name is
				    `--surface` — so the background resolved to nothing and every
				    line in this panel was painted straight onto the brand gradient
				    behind it. The AA sweep read grounds of rgb(142,91,245) and
				    scored the eyebrow at 1.42:1 dark and 1.10:1 light. The
				    geometry harness passed the same build, because geometry cannot
				    see colour.
				    `--surface` would fix the name and leave a 0.85-alpha face over
				    a gradient, i.e. a ratio computed against a colour that depends
				    on what is behind it — the D-057 trap. `--surface-modal` is
				    fully opaque, already declared in both themes, and already
				    named for exactly this: the certificate lightbox. No new token. */}
				<div className="rounded-[19px] bg-[var(--surface-modal)] px-6 py-8 text-center sm:px-10 sm:py-10">
					<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--accent-marker)]">
						Credential
					</p>

					<h3 className="mt-3 text-lg font-bold leading-snug text-[var(--text-strong)] sm:text-xl">
						{certificate.title}
					</h3>

					<p className="mt-2 text-sm text-[var(--text-muted)]">
						{certificate.issuer}
						<span aria-hidden="true"> · </span>
						{longDate(certificate.issuedDate)}
					</p>

					{statement && (
						<blockquote className="mx-auto mt-6 max-w-xl border-t border-[var(--border)] pt-6 text-sm italic leading-relaxed text-[var(--text-secondary)]">
							{statement}
						</blockquote>
					)}

					{binary && (
						<div className="mt-8">
							<CertificateLightbox
								src={binary}
								alt={certificate.alt}
								title={certificate.title}
								triggerLabel={`Enlarge the certificate: ${certificate.title}`}
								triggerClassName="group block w-full rounded-[12px] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
							>
								{/* `alt=""` HERE AND THE REAL ALT ON THE ENLARGED
								    IMAGE. This picture is inside the button, so its
								    alt text would append to the button's accessible
								    name — the reader would hear the whole
								    certificate description before the word "button"
								    (D-095's rule for the workshop glimpse). */}
								<img
									src={binary}
									alt=""
									aria-hidden="true"
									loading="lazy"
									decoding="async"
									className="mx-auto w-full rounded-[12px] border border-[var(--border)] transition-transform duration-200 group-hover:scale-[1.01]"
								/>
							</CertificateLightbox>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
